import { createReducer } from '@reduxjs/toolkit';
import { saveKeyFeatureScreenShot } from 'actionCreators/screenshot';
import { bankDetailsSuccessResponse } from 'actionCreators/search';
import { resetPrimaryApplicantInfo } from 'actionCreators/config';
import { getCompleteCaseSuccessResponse } from 'actionCreators/complete';
import { authCodeSuccessResponse, updateJointLinkCode } from 'actionCreators/authCode';
import { authCodeFailureResponse } from 'actionCreators/error';
import { bankDetailsFailureResponse } from 'actionCreators/error';
import { userNameSubmitted } from 'actionCreators/user';
import {
  createCaseSuccess,
  updateCaseSuccessResponse,
  resetUpdateStatus,
  getCaseSuccessResponse,
  showUserNameExists,
  setCaseId,
  setMitekUrl,
  resetCaseInfo,
  createCaseFailedResponse,
  submitCaseSuccessResponse,
  failedIdentityCheck,
  setCaseCreatedDate,
  offerDecisionSuccessResponse,
  updateDIPAttemptCount,
  saveMoveInDateForReprocess,
  setReduxFormOldData,
  resetReduxFormOldData,
  mapFields,
  setChallengeRequestResponse,
  setMitekOutcomeResponse,
  fetchIllustrationBasedOnDIPCaseSuccess,
  createDIPCaseSuccess
} from 'actionCreators/case';

const initialState = {
  caseId: null,
  caseInfo: null,
  updateCaseDone: false,
  showUserNameExists: false,
  completeInfo: null,
  authInfo: null,
  authCodeFailureResponse: null,
  bankInfo: null,
  keyFeature: null,
  loanAgreement: null,
  bankDetailsFailureResponse: null,
  linkCodeCaseFailed: 0,
  caseSubmitted: false,
  isUserNameSubmitted: false,
  isIDCheckFailed: false,
  caseCreatedDate: null,
  isOfferDecisionSuccess: false,
  backNavGetCase: false,
  dipCaseInfo: null,
  dipSubmitAttempt: 0,
  dipReprocessMoveInDate: null,
  oldFormData: null,
  isChallengeRequested: false,
  isOutcomeReceived: false,
  mitekUrl: null
};

const caseDetailsReducer = createReducer(initialState, (builder) => {
  builder.addCase('setQueryParam', (state, action) => {
    const { caseId } = action.payload;
    caseId && (state.caseId = caseId);
  });

  builder.addCase(createCaseSuccess, (state, action) => {
    state.caseId = action.payload.reference_id;
  });

  builder.addCase(updateCaseSuccessResponse, (state) => {
    state.showUserNameExists = false;
    state.updateCaseDone = true;
    state.backNavGetCase = false;
  });

  builder.addCase(resetUpdateStatus, (state) => {
    state.updateCaseDone = false;
  });

  builder.addCase(getCaseSuccessResponse, (state, action) => {
    state.caseInfo = action.payload;
    state.backNavGetCase = true;
  });

  builder.addCase(setCaseCreatedDate, (state, action) => {
    state.caseCreatedDate = action.payload;
  });

  builder.addCase(showUserNameExists, (state, action) => {
    state.isUserNameSubmitted = false;
    state.showUserNameExists = action.payload.showUserNameExists;
  });

  builder.addCase(bankDetailsSuccessResponse, (state, action) => {
    state.bankInfo = action.payload;
  });

  builder.addCase(bankDetailsFailureResponse, (state, action) => {
    state.bankDetailsFailureResponse = action.payload;
  });

  builder.addCase(getCompleteCaseSuccessResponse, (state, action) => {
    state.completeInfo = action.payload;
  });

  builder.addCase(authCodeSuccessResponse, (state, action) => {
    state.authInfo = action.payload;
  });

  builder.addCase(authCodeFailureResponse, (state, action) => {
    state.authCodeFailureResponse = action.payload;
  });

  builder.addCase(saveKeyFeatureScreenShot, (state, action) => {
    state.keyFeature = action.payload;
  });

  builder.addCase(setCaseId, (state, action) => {
    state.caseId = action.payload;
  });

  builder.addCase(setMitekUrl, (state, action) => {
    state.mitekUrl = action.payload;
  });

  builder.addCase(resetCaseInfo, () => initialState);

  builder.addCase(createCaseFailedResponse, (state) => {
    state.linkCodeCaseFailed = state.linkCodeCaseFailed + 1;
  });

  builder.addCase(updateJointLinkCode, (state) => {
    return {
      ...state,
      linkCodeCaseFailed: state.linkCodeCaseFailed > 1 ? 1 : state.linkCodeCaseFailed
    };
  });

  builder.addCase(submitCaseSuccessResponse, (state) => {
    return {
      ...state,
      caseSubmitted: true
    };
  });

  builder.addCase(resetPrimaryApplicantInfo, (state) => {
    return {
      ...state,
      caseId: null,
      updateCaseDone: false,
      caseInfo: null,
      isUserNameSubmitted: false,
      isIDCheckFailed: false
    };
  });

  builder.addCase(userNameSubmitted, (state) => {
    state.isUserNameSubmitted = true;
  });

  builder.addCase(failedIdentityCheck, (state) => {
    state.isIDCheckFailed = true;
  });

  builder.addCase(offerDecisionSuccessResponse, (state) => {
    state.isOfferDecisionSuccess = true;
  });

  builder.addCase('case/requestBlackboxFingerprint', (state) => {
    state.currentBlackboxAttempt = state.currentBlackboxAttempt + 1;
  });

  builder.addCase(createDIPCaseSuccess, (state, action) => {
    state.dipCaseInfo = action.payload;
  });

  builder.addCase(fetchIllustrationBasedOnDIPCaseSuccess, (state, action) => {
    /**
     * Store the illustration retrieved in response to getting a
     * successful Decision in Principle (DIP) Outcome
     * @note The illustration has to be retrieved as a seperate
     * request from the Decision in Principle case creation.
     * It is not bundled into the same response.
     */
    state.dipCaseIllustration = action.payload;
  });

  builder.addCase(updateDIPAttemptCount, (state, action) => {
    state.dipSubmitAttempt = state.dipSubmitAttempt + action.payload;
  });

  builder.addCase(saveMoveInDateForReprocess, (state, action) => {
    state.dipReprocessMoveInDate = action.payload;
  });

  builder.addCase(mapFields, (state, action) => {
    state.ntbToDipForm = action.payload;
  });

  builder.addCase(setReduxFormOldData, (state, action) => {
    state.oldFormData = action.payload;
  });

  builder.addCase(resetReduxFormOldData, (state) => {
    state.oldFormData = null;
  });

  builder.addCase(setChallengeRequestResponse, (state, action) => {
    state.isChallengeRequested = action.payload;
  });

  builder.addCase(setMitekOutcomeResponse, (state, action) => {
    state.isOutcomeReceived = action.payload;
  });
});
export { caseDetailsReducer };
